import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
    useTheme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'

import { Container } from '../../../../components'
import { useTimezone } from '../../../../contexts/timezone'
import { SelectorPopover } from '../../../../components/TemplateScheduleGantt/Components/SelectorPopover'
import { axiosInstance } from '../../../../helpers'
import { useUser } from '../../../../hooks'
import { usePopover } from '../../../../hooks/usePopover'
import {
    AddTemplateServiceParams,
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceParams,
    DeleteTemplateServiceVendorParams,
    SetTemplateUnitsParams,
    TemplateWorkordersByPhaseEntry,
    UnitCoverageByPhaseEntry,
    UpdateEzScheduleParams,
    UpdateTemplateServiceParams,
    UpdateTemplateServiceVendorParams,
} from '../../../../hooks/useEzSchedule/types'
import {
    AreaConfig,
    AreaStatusTag,
    ezScheduleDetails,
    Folder,
    ListVendor,
    ModelMap,
    Service,
    TemplateWorkorder,
    UnitConfig,
    UnitCoverage,
} from '../../../../models'
import { WalkMobilePreview } from './WalkMobilePreview'
import { WalkScheduleStep } from './walkSchedule/WalkScheduleStep'
import { WalkTimelinePicker } from './walkSchedule/WalkTimelinePicker'
import {
    clampedVendorUpdates,
    formatTimelineChip,
    workRangeMessage,
} from './walkSchedule/timelineRules'
import { WalkSchedulerProgress } from './WalkSchedulerProgress'
import { WalkSchedulerFrame } from './WalkSchedulerStepper'
import { ServiceRow } from './components/ServiceRow'
import { useAutoScheduleStyles } from './styles'
import { SchedulePhase } from './types'

interface UnitInspectionRow {
    id: number
    complete_status: boolean
    unit_id: number
    unit_name?: string
}

interface InspectionFlag {
    id: number
    walk_scheduler: boolean
}

export type WalkView = 'setup' | 'progress' | 'schedule'

type WalkEditMode = 'maintain' | 'clear' | 'free'

const parseZoned = (value: string | null | undefined, zone: string) => {
    if (!value) return null
    const parsed = DateTime.fromISO(value).setZone(zone)
    return parsed.isValid ? parsed : null
}

const pickerDate = (date: DateTime | null) =>
    date?.setZone('local', { keepLocalTime: true }).toJSDate() ?? null

const LORI_U_APARTMENT_ID = 6957

const derivedWalkView = (
    committed: boolean,
    inspectionComplete: boolean,
): WalkView => {
    if (!committed) return 'setup'
    if (!inspectionComplete) return 'progress'
    return 'schedule'
}

interface Props {
    inspectionId: number
    onExit: () => void
    walkView: WalkView | null
    onWalkViewChange: (view: WalkView) => void
    onWalkBackChange: (handler: (() => void) | null) => void
    ezSchedule?: ezScheduleDetails
    phaseId?: number
    ezScheduleLoading: boolean
    infrastructure: Folder
    schedulerInfrastructure?: Folder
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    vendorList: ListVendor[]
    serviceList: Service[]
    onPhaseChange: (phase: SchedulePhase) => void
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    updateEzScheduleAsync: (params: UpdateEzScheduleParams) => Promise<unknown>
    addTemplateService: (params: AddTemplateServiceParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    addTemplateServiceVendor: (params: AddTemplateServiceVendorParams) => void
    deleteTemplateServiceVendor: (
        params: DeleteTemplateServiceVendorParams,
    ) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    setTemplateUnits: (params: SetTemplateUnitsParams) => Promise<unknown>
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    generateTemplateWorkorders: () => void
    generateTemplateWorkordersLoading: boolean
    templateWorkordersLoading: boolean
    unitCoverage?: UnitCoverage
    templateWorkordersByPhase: TemplateWorkordersByPhaseEntry[]
    unitCoverageByPhase: UnitCoverageByPhaseEntry[]
    generateWorkorders: (
        phaseIds: number[],
        deleteExistingWorkorders: boolean,
        creationSource: string,
    ) => Promise<{ status: number }>
    refetchTemplateWorkorders: () => void
    templateWorkorders: TemplateWorkorder[]
    reloadScheduleWorkorders: () => void | Promise<void>
    workordersLoading: boolean
    onCloseModal: () => void
    onAfterSchedule?: () => void
}

export const WalkScheduler = (props: Props) => {
    const {
        inspectionId,
        onExit,
        walkView,
        onWalkViewChange,
        onWalkBackChange,
        ezSchedule,
        phaseId,
        ezScheduleLoading,
        unitConfigMap,
        areaConfigMap,
        areaStatusTagMap,
        vendorList,
        serviceList,
        setTemplateUnits,
        addTemplateService,
        deleteTemplateService,
        addTemplateServiceVendor,
        deleteTemplateServiceVendor,
        updateTemplateServiceVendor,
        updateTemplateService,
        updateEzSchedule,
        updateEzScheduleAsync,
        templateWorkorders,
    } = props

    const classes = useAutoScheduleStyles()
    const theme = useTheme()
    const { currentTimezone } = useTimezone()
    const { rootUser, workspaceUser } = useUser()
    const servicePopover = usePopover()
    const canFillWalkDemo =
        (rootUser?.email || '').toLowerCase().endsWith('@ezos.co') &&
        workspaceUser?.active_workspace?.id === LORI_U_APARTMENT_ID

    const [loadingInspection, setLoadingInspection] = useState(true)
    const [saving, setSaving] = useState(false)
    const [walkScheduler, setWalkScheduler] = useState(false)
    const [unitInspections, setUnitInspections] = useState<UnitInspectionRow[]>(
        [],
    )
    const [resetOpen, setResetOpen] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [editMode, setEditMode] = useState<WalkEditMode | null>(null)
    const [editWarningOpen, setEditWarningOpen] = useState(false)
    const [leaveOpen, setLeaveOpen] = useState(false)
    const [clearing, setClearing] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [draftStart, setDraftStart] = useState<DateTime | null>(null)
    const [draftEnd, setDraftEnd] = useState<DateTime | null>(null)
    const [editingTimeline, setEditingTimeline] = useState(false)
    const [timelineSaving, setTimelineSaving] = useState(false)
    const [unitSyncError, setUnitSyncError] = useState(false)
    const unitSyncKey = useRef<string | null>(null)
    const serviceBaselineRef = useRef<Set<number>>(new Set())
    const handleBackRef = useRef<() => void>(() => {})

    const loadInspection = async () => {
        setLoadingInspection(true)
        setLoadError(false)
        try {
            const [inspectionRes, unitRes] = await Promise.all([
                axiosInstance.get(`inspection/${inspectionId}/`),
                axiosInstance.get(
                    `inspection/unit-inspection/get_schedule_unit_inspections/`,
                    { params: { inspection_id: inspectionId } },
                ),
            ])
            const inspection = inspectionRes.data as InspectionFlag
            setWalkScheduler(Boolean(inspection.walk_scheduler))
            setUnitInspections(unitRes.data as UnitInspectionRow[])
        } catch {
            setLoadError(true)
            toast.error('Failed to load the inspection for Walk Scheduler')
            return false
        } finally {
            setLoadingInspection(false)
        }
        return true
    }

    useEffect(() => {
        void loadInspection()
    }, [inspectionId])

    const completedUnits = useMemo(
        () => unitInspections.filter((unit) => unit.complete_status),
        [unitInspections],
    )
    const inspectionComplete =
        unitInspections.length > 0 &&
        completedUnits.length === unitInspections.length

    const resolvedView = useMemo(() => {
        if (walkView) return walkView
        if (loadingInspection) return null
        return derivedWalkView(walkScheduler, inspectionComplete)
    }, [walkView, loadingInspection, walkScheduler, inspectionComplete])

    useEffect(() => {
        if (!resolvedView || walkView != null) return
        onWalkViewChange(resolvedView)
    }, [resolvedView, walkView, onWalkViewChange])

    useEffect(() => {
        if (loadingInspection || walkScheduler) {
            setResetOpen(false)
            return
        }
        setResetOpen(completedUnits.length > 0)
    }, [loadingInspection, walkScheduler, completedUnits.length])

    const selectedPhase = useMemo(() => {
        if (!ezSchedule || phaseId === undefined) return null
        return (
            ezSchedule.phases.find((phase) => phase.phase?.id === phaseId) ??
            null
        )
    }, [ezSchedule, phaseId])

    const phaseTemplateServices = selectedPhase?.template_services ?? []
    const workStart = parseZoned(
        !ezSchedule
            ? null
            : phaseId === undefined
            ? ezSchedule.work_start_date
            : selectedPhase?.work_start_date,
        currentTimezone,
    )
    const workEnd = parseZoned(
        !ezSchedule
            ? null
            : phaseId === undefined
            ? ezSchedule.work_end_date
            : selectedPhase?.work_end_date,
        currentTimezone,
    )
    const scheduleStart = parseZoned(ezSchedule?.start_date, currentTimezone)
    const scheduleEnd = parseZoned(ezSchedule?.end_date, currentTimezone)
    const hasTimeline = Boolean(workStart && workEnd)

    const serviceAdded =
        editMode === 'maintain' &&
        phaseTemplateServices.some(
            (templateService) =>
                templateService.id > 0 &&
                !serviceBaselineRef.current.has(templateService.id),
        )

    const includedUnitIds = useMemo(() => {
        return (selectedPhase?.template_units ?? [])
            .filter((templateUnit) => templateUnit.is_included)
            .map((templateUnit) => templateUnit.unit.id)
            .sort((left, right) => left - right)
    }, [selectedPhase])

    const inspectionUnitIds = useMemo(() => {
        return unitInspections
            .map((unit) => unit.unit_id)
            .sort((left, right) => left - right)
    }, [unitInspections])

    const unitsMatch = inspectionUnitIds.join(',') === includedUnitIds.join(',')

    useEffect(() => {
        if (
            resolvedView !== 'schedule' ||
            !walkScheduler ||
            unitsMatch ||
            phaseId === undefined
        ) {
            return
        }
        const syncKey = inspectionUnitIds.join(',')
        if (unitSyncKey.current === syncKey) {
            return
        }
        unitSyncKey.current = syncKey
        void setTemplateUnits({
            ordered_units: unitInspections.map((unit, index) => ({
                unitId: unit.unit_id,
                order: index,
            })),
        }).catch(() => {
            unitSyncKey.current = null
            setUnitSyncError(true)
            toast.error('Failed to apply inspection units')
        })
    }, [
        resolvedView,
        walkScheduler,
        unitsMatch,
        phaseId,
        inspectionUnitIds,
        unitInspections,
        setTemplateUnits,
    ])

    useEffect(() => {
        onWalkBackChange(() => {
            handleBackRef.current()
        })
        return () => onWalkBackChange(null)
    }, [onWalkBackChange])

    const availableServices = useMemo(() => {
        return serviceList.filter((service) =>
            vendorList.some((vendor) =>
                vendor.services.some(
                    (vendorService) => vendorService.id === service.id,
                ),
            ),
        )
    }, [serviceList, vendorList])

    const servicesMissingVendors = phaseTemplateServices.some(
        (templateService) =>
            templateService.template_service_vendors.length === 0,
    )
    const servicesReady =
        phaseTemplateServices.length > 0 &&
        !servicesMissingVendors &&
        phaseTemplateServices.every((templateService) => templateService.id > 0)
    const canSave = !walkScheduler && servicesReady
    const canEditSave = walkScheduler && servicesReady

    const beginEdit = (mode: WalkEditMode) => {
        serviceBaselineRef.current = new Set(
            phaseTemplateServices
                .map((templateService) => templateService.id)
                .filter((id) => id > 0),
        )
        setEditMode(mode)
        setLeaveOpen(false)
        setEditWarningOpen(false)
        onWalkViewChange('setup')
    }

    const handleSaveTimeline = async () => {
        if (!draftStart || !draftEnd) return
        const start = draftStart.startOf('day')
        const end = draftEnd.endOf('day')
        setTimelineSaving(true)
        try {
            await updateEzScheduleAsync({
                work_start_date: start,
                work_end_date: end,
            })
            clampedVendorUpdates(
                phaseTemplateServices,
                start,
                end,
                currentTimezone,
            ).forEach((update) => updateTemplateServiceVendor(update))
            setEditingTimeline(false)
        } catch {
            toast.error('Could not save the project timeline.')
        } finally {
            setTimelineSaving(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            await axiosInstance.patch(`inspection/${inspectionId}/`, {
                walk_scheduler: true,
            })
            setWalkScheduler(true)
            setEditMode(null)
            onWalkViewChange('progress')
            toast.success('Walk Scheduler saved')
        } catch {
            toast.error('Failed to save Walk Scheduler')
        } finally {
            setSaving(false)
        }
    }

    const handleEditSave = async () => {
        setSaving(true)
        try {
            if (editMode === 'maintain' && serviceAdded) {
                await axiosInstance.post(
                    `inspection/${inspectionId}/reopen_completed_units/`,
                )
                const loaded = await loadInspection()
                if (!loaded) return
            }
            setEditMode(null)
            onWalkViewChange('progress')
        } catch {
            toast.error('Failed to save Walk Scheduler')
        } finally {
            setSaving(false)
        }
    }

    const handleReset = async () => {
        setResetting(true)
        try {
            await axiosInstance.post(
                `inspection/${inspectionId}/reset_completed_units/`,
            )
            setResetOpen(false)
            await loadInspection()
        } catch {
            toast.error('Failed to clear completed unit inspections')
        } finally {
            setResetting(false)
        }
    }

    const handleFillDemo = async () => {
        try {
            await axiosInstance.post(
                `inspection/${inspectionId}/fill_walk_demo/`,
            )
        } catch {
            toast.error('Failed to fill remaining units')
            throw new Error('fill')
        }
        const loaded = await loadInspection()
        if (!loaded) {
            throw new Error('reload')
        }
    }

    const handleClear = async () => {
        setClearing(true)
        try {
            await axiosInstance.post(
                `inspection/${inspectionId}/clear_walk_progress/`,
            )
            beginEdit('clear')
            await loadInspection()
        } catch {
            toast.error('Failed to clear completed units')
        } finally {
            setClearing(false)
        }
    }

    handleBackRef.current = () => {
        if (saving || clearing || !resolvedView) return
        if (resolvedView === 'schedule') {
            onWalkViewChange('progress')
            return
        }
        if (resolvedView === 'progress') {
            if (completedUnits.length > 0) {
                setEditWarningOpen(true)
                return
            }
            beginEdit('free')
            return
        }
        if (walkScheduler) {
            if (editMode === 'maintain' && serviceAdded) {
                setLeaveOpen(true)
                return
            }
            setEditMode(null)
            onWalkViewChange('progress')
            return
        }
        onExit()
    }

    if (loadError) {
        return (
            <Container className={classes.root}>
                <Container className={classes.loadingContainer}>
                    <Typography>
                        The inspection for Walk Scheduler could not be loaded.
                    </Typography>
                </Container>
            </Container>
        )
    }

    if (
        loadingInspection ||
        ezScheduleLoading ||
        !ezSchedule ||
        !resolvedView
    ) {
        return (
            <Container className={classes.root}>
                <Container className={classes.loadingContainer}>
                    <CircularProgress />
                </Container>
            </Container>
        )
    }

    let body: React.ReactNode
    if (resolvedView === 'schedule') {
        if (unitSyncError) {
            body = (
                <Container className={classes.root}>
                    <Container className={classes.loadingContainer}>
                        <Typography>
                            The inspection units could not be applied to this
                            project.
                        </Typography>
                    </Container>
                </Container>
            )
        } else if (!unitsMatch) {
            body = (
                <Container className={classes.root}>
                    <Container className={classes.loadingContainer}>
                        <CircularProgress />
                    </Container>
                </Container>
            )
        } else {
            body = (
                <WalkScheduleStep
                    ezSchedule={ezSchedule}
                    phaseId={phaseId}
                    inspectionId={inspectionId}
                    vendorList={vendorList}
                    updateEzSchedule={updateEzSchedule}
                    updateEzScheduleAsync={updateEzScheduleAsync}
                    addTemplateServiceVendor={addTemplateServiceVendor}
                    deleteTemplateService={deleteTemplateService}
                    deleteTemplateServiceVendor={deleteTemplateServiceVendor}
                    updateTemplateServiceVendor={updateTemplateServiceVendor}
                    updateTemplateService={updateTemplateService}
                    setTemplateUnits={setTemplateUnits}
                    infrastructure={props.infrastructure}
                    unitConfigMap={unitConfigMap}
                    areaConfigMap={areaConfigMap}
                    areaStatusTagMap={areaStatusTagMap}
                    generateTemplateWorkorders={
                        props.generateTemplateWorkorders
                    }
                    generateTemplateWorkordersLoading={
                        props.generateTemplateWorkordersLoading
                    }
                    templateWorkordersLoading={props.templateWorkordersLoading}
                    unitCoverage={props.unitCoverage}
                    templateWorkordersByPhase={props.templateWorkordersByPhase}
                    unitCoverageByPhase={props.unitCoverageByPhase}
                    generateWorkorders={props.generateWorkorders}
                    templateWorkorders={templateWorkorders}
                    reloadScheduleWorkorders={props.reloadScheduleWorkorders}
                    workordersLoading={props.workordersLoading}
                    onCloseModal={props.onCloseModal}
                    onAfterSchedule={props.onAfterSchedule}
                />
            )
        }
    } else if (resolvedView === 'progress') {
        body = (
            <WalkSchedulerProgress
                services={phaseTemplateServices}
                unitInspections={unitInspections}
                canFillDemo={canFillWalkDemo}
                onFillDemo={handleFillDemo}
                onContinue={() => onWalkViewChange('schedule')}
            />
        )
    } else if (!hasTimeline || editingTimeline) {
        const boundsLabel =
            scheduleStart && scheduleEnd
                ? `Schedule dates ${scheduleStart.toFormat(
                      'LLL d',
                  )} – ${scheduleEnd.toFormat('LLL d, yyyy')}`
                : null
        body = (
            <WalkTimelinePicker
                zone={currentTimezone}
                start={draftStart}
                end={draftEnd}
                minDate={pickerDate(scheduleStart)}
                maxDate={pickerDate(scheduleEnd)}
                boundsLabel={boundsLabel}
                error={workRangeMessage(
                    draftStart ? draftStart.startOf('day') : null,
                    draftEnd ? draftEnd.endOf('day') : null,
                    { scheduleStart, scheduleEnd },
                )}
                saving={timelineSaving}
                onChange={(start, end) => {
                    setDraftStart(start)
                    setDraftEnd(end)
                }}
                onSave={() => {
                    void handleSaveTimeline()
                }}
                onCancel={hasTimeline ? () => setEditingTimeline(false) : null}
            />
        )
    } else {
        body = (
            <Container
                className={classes.root}
                style={{ padding: theme.spacing(2, 4, 4) }}
            >
                <div className={classes.walkSetupRow}>
                    <Container
                        className={classes.walkSetup}
                        style={{ padding: theme.spacing(1, 2, 2) }}
                    >
                        <div style={{ padding: theme.spacing(1, 0, 1) }}>
                            <Typography
                                variant="h6"
                                className={classes.walkSetupTitle}
                            >
                                Inspection Turn Scheduler
                            </Typography>
                            {editMode === 'maintain' && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Add a service, then save. Completed units
                                    are marked incomplete so they can be walked
                                    for that service. Choices already made stay
                                    selected.
                                </Typography>
                            )}
                            {editMode === 'clear' && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Completed units were cleared. Update the
                                    services, then save.
                                </Typography>
                            )}
                            {editMode === 'free' && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Update the services and vendors for this
                                    walk.
                                </Typography>
                            )}
                        </div>
                        {workStart && workEnd && (
                            <div style={{ marginTop: 12 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        setDraftStart(workStart)
                                        setDraftEnd(workEnd)
                                        setEditingTimeline(true)
                                    }}
                                    style={{ textTransform: 'none' }}
                                >
                                    {formatTimelineChip(workStart, workEnd)}
                                </Button>
                            </div>
                        )}
                        <Container className={classes.walkServiceHeader}>
                            <Typography
                                variant="h6"
                                className={classes.walkServiceTitle}
                            >
                                Select Services
                            </Typography>
                            <Button
                                onClick={servicePopover.handleOpen}
                                className={`${classes.addServiceButton} ${classes.walkAddServiceButton}`}
                                startIcon={<AddIcon />}
                                disabled={phaseId === undefined}
                            >
                                Add Service
                            </Button>
                        </Container>
                        <Container
                            className={classes.servicesScrollContainer}
                            scrollY
                            style={{ flex: 1, minHeight: 0 }}
                        >
                            {phaseTemplateServices.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    className={classes.walkEmptyCopy}
                                >
                                    Choose the services and vendors for this
                                    turn. Saving tells the inspection to collect
                                    which areas need each service.
                                </Typography>
                            )}
                            {phaseTemplateServices.map(
                                (templateService, index) => (
                                    <ServiceRow
                                        key={templateService.id}
                                        index={index}
                                        serviceCount={
                                            phaseTemplateServices.length
                                        }
                                        templateService={templateService}
                                        theme={theme}
                                        vendorList={vendorList}
                                        readOnly={false}
                                        showBudget
                                        showAutoSelect
                                        deleteTemplateService={
                                            deleteTemplateService
                                        }
                                        updateTemplateService={
                                            updateTemplateService
                                        }
                                        addTemplateServiceVendor={
                                            addTemplateServiceVendor
                                        }
                                        deleteTemplateServiceVendor={
                                            deleteTemplateServiceVendor
                                        }
                                        updateTemplateServiceVendor={
                                            updateTemplateServiceVendor
                                        }
                                    />
                                ),
                            )}
                        </Container>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={
                                saving ||
                                (walkScheduler ? !canEditSave : !canSave)
                            }
                            onClick={() => {
                                void (walkScheduler
                                    ? handleEditSave()
                                    : handleSave())
                            }}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                        {phaseTemplateServices.length > 0 &&
                            !(walkScheduler ? canEditSave : canSave) && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Assign at least one vendor to every service
                                    before saving.
                                </Typography>
                            )}
                    </Container>
                    <WalkMobilePreview
                        className={classes.walkPhoneColumn}
                        infrastructure={
                            props.schedulerInfrastructure ??
                            props.infrastructure
                        }
                        templateServices={phaseTemplateServices}
                    />
                </div>
                <SelectorPopover
                    theme={theme}
                    popoverState={servicePopover}
                    data={availableServices}
                    label="Select Service"
                    onSubmit={(selectedId) => {
                        const serviceExists = phaseTemplateServices.some(
                            (templateService) =>
                                templateService.service.id === selectedId,
                        )
                        if (serviceExists) return
                        addTemplateService({
                            serviceId: selectedId,
                            serviceName:
                                availableServices.find(
                                    (service) => service.id === selectedId,
                                )?.name ?? '',
                            scheduleId: ezSchedule.id,
                        })
                    }}
                />
            </Container>
        )
    }

    return (
        <WalkSchedulerFrame>
            {body}
            <Dialog open={resetOpen} onClose={onExit} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Completed units use a different process
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This inspection already has completed units. Those were
                        finished with the standard inspection, which does not
                        store the service and area choices Walk Scheduler needs.
                        Continuing clears the inspection results for those units
                        so they can be walked again.
                    </DialogContentText>
                    {completedUnits.map((unit) => (
                        <Typography key={unit.id} variant="body2">
                            {unit.unit_name ?? `Unit ${unit.unit_id}`}
                        </Typography>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onExit} disabled={resetting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            void handleReset()
                        }}
                        color="primary"
                        variant="contained"
                        disabled={resetting}
                    >
                        {resetting ? 'Clearing…' : 'Clear and continue'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={editWarningOpen}
                onClose={() => {
                    if (!clearing) setEditWarningOpen(false)
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Units on this walk are already complete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You can keep their walk selections and inspection
                        results, or clear those units and start them over.
                        Keeping the data and adding a service marks the
                        completed units incomplete when you save, so they can be
                        walked for that service. Choices already made stay
                        selected. Clearing deletes walk selections and
                        inspection results for completed units. The walk stays
                        on.
                    </DialogContentText>
                    {completedUnits.map((unit) => (
                        <Typography key={unit.id} variant="body2">
                            {unit.unit_name ?? `Unit ${unit.unit_id}`}
                        </Typography>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setEditWarningOpen(false)}
                        disabled={clearing}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            void handleClear()
                        }}
                        disabled={clearing}
                    >
                        {clearing ? 'Clearing…' : 'Clear and start over'}
                    </Button>
                    <Button
                        onClick={() => beginEdit('maintain')}
                        color="primary"
                        variant="contained"
                        disabled={clearing}
                    >
                        Maintain data
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={leaveOpen}
                onClose={() => setLeaveOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save to reopen completed units</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You added a service. Save to mark completed units
                        incomplete so they can be walked for that service. Their
                        earlier choices stay in place. Leaving without saving
                        keeps those units complete.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setLeaveOpen(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setLeaveOpen(false)
                            setEditMode(null)
                            onWalkViewChange('progress')
                        }}
                        disabled={saving}
                    >
                        Leave
                    </Button>
                    <Button
                        onClick={() => {
                            setLeaveOpen(false)
                            void handleEditSave()
                        }}
                        color="primary"
                        variant="contained"
                        disabled={saving || !canEditSave}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </WalkSchedulerFrame>
    )
}
